import React, { useEffect, useRef, useState, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { PREVIEW_SHELL, applyToDocument } from '@/lib/livePreview';
import { toPages, homePath, findPage, resolveLink } from '@/lib/sitePages';
import {
  injectEditorStyle, removeEditorStyle, pickAt, select,
  serializeBody, clearSelection,
} from '@/lib/visualEdit';

/**
 * An iframe that loads its shell once and is then updated in place, so a
 * generation streams in visibly without ever reloading the document. See
 * src/lib/livePreview.js for why srcDoc swapping is avoided.
 *
 * It also owns navigation *inside* the generated site. The frame has no origin
 * of its own, so an ordinary click on `<a href="about.html">` resolves against
 * builderslab.net and navigates the frame out of the preview entirely — the
 * "clicking a link takes me to BuildersLab" bug. Every click is intercepted
 * here and turned into a page switch, a scroll, a new tab, or nothing at all.
 *
 * @param {object} props
 * @param {Array<{path:string,html:string}>} [props.pages] the generated pages
 * @param {string} [props.html]      single-page fallback when `pages` is absent
 * @param {string} [props.css]       stylesheet shared by every page
 * @param {string} [props.js]        script shared by every page; runs once the page settles
 * @param {string} [props.icon]      generated SVG mark, used as the frame's favicon
 * @param {string} [props.activePath] controlled current page
 * @param {Function} [props.onNavigate] called with the new path on an internal link
 * @param {Function} [props.onBlockedLink] called with the href of a dead link
 * @param {boolean} [props.isGenerating] throttles updates while the stream is open
 * @param {object}  [props.wireframe] a first build's structure — pages, sections,
 *   and the page being written — drawn below the content until it arrives. Only
 *   honoured while generating; see src/lib/wireframe.js
 * @param {string} [props.title]     iframe title, for accessibility
 * @param {string} [props.className] classes applied to the iframe
 * @param {boolean} [props.interactive] set false to make the frame click-through
 * @param {boolean} [props.editMode]   click selects an element instead of navigating
 * @param {Function} [props.onSelect]  called with the selected element (or null)
 * @param {Function} [props.onEditDirty] called after any in-frame mutation
 *
 * In edit mode the frame stops re-rendering from props: the document itself
 * becomes the source of truth until the caller reads it back with
 * `ref.current.serialize()`. Morphing while someone is typing would overwrite
 * their edit on the next pass.
 */
const LivePreviewFrame = forwardRef(function LivePreviewFrame({
  pages,
  html,
  css,
  js,
  icon,
  activePath,
  onNavigate,
  onBlockedLink,
  isGenerating,
  wireframe,
  title,
  className,
  interactive = true,
  editMode = false,
  onSelect,
  onEditDirty,
}, ref) {
  const frameRef  = useRef(null);
  const timerRef  = useRef(null);
  const latestRef = useRef(null);
  // Whether a previous run's scripts are already bound to nodes on the page.
  const scriptsRanRef = useRef(false);
  // The page currently painted into the frame, so a change can force a clean
  // render instead of morphing one page's DOM into an unrelated one.
  const renderedPathRef = useRef(null);
  const pendingHashRef  = useRef('');
  const [ready, setReady] = useState(false);
  // Used only when the caller does not control the page.
  const [internalPath, setInternalPath] = useState(null);

  // Memoised so the click-interception effect below keeps a stable dependency
  // and does not resubscribe on every streamed chunk.
  const pageList = useMemo(() => toPages({ pages, html }), [pages, html]);
  const current  = activePath ?? internalPath ?? homePath(pageList);
  const page     = findPage(pageList, current);

  latestRef.current = {
    html: page?.html || '', css, js, icon, path: page?.path || null,
    // Never outlives the run: the final pass after the stream closes must be
    // the one that takes the frame down.
    wireframe: isGenerating ? wireframe || null : null,
  };

  const navigate = useCallback((path, hash) => {
    pendingHashRef.current = hash || '';
    if (activePath === undefined) setInternalPath(path);
    onNavigate?.(path);
  }, [activePath, onNavigate]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Read the edited document back out. The caller owns persistence — the frame
  // deliberately has no idea the API exists.
  useImperativeHandle(ref, () => ({
    serialize: () => serializeBody(frameRef.current?.contentDocument),
    getDocument: () => frameRef.current?.contentDocument || null,
    clearSelection: () => clearSelection(frameRef.current?.contentDocument),
  }), []);

  // ── Render / stream into the live document ────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    // In edit mode the DOM is authoritative. Re-applying props here would morph
    // the user's in-progress edit back to the last saved source. Unless nothing
    // has been drawn yet: Edit clicked as the page opened, before the frame's
    // first render, used to leave the preview blank for the whole session,
    // because every render after that was skipped. So the first is let through.
    if (editMode && renderedPathRef.current !== null) return;

    const apply = (isFinal) => {
      const doc = frameRef.current?.contentDocument;
      if (!doc?.body) return;

      const { path } = latestRef.current;
      // Switching pages is a replacement, not a diff: clear the body, run the
      // new page's scripts, and start at the top like a real navigation.
      const switched = renderedPathRef.current !== null && renderedPathRef.current !== path;
      const full = isFinal || switched;

      if (switched) doc.body.innerHTML = '';

      scriptsRanRef.current = applyToDocument(doc, latestRef.current, {
        isFinal: full,
        // A page switch always starts from an empty body, so nothing from the
        // previous page can still be bound — no need to wipe again.
        scriptsAlreadyRan: switched ? false : scriptsRanRef.current,
        wireframe: latestRef.current.wireframe,
      });
      renderedPathRef.current = path;

      if (!switched) return;

      const hash = pendingHashRef.current;
      pendingHashRef.current = '';
      const target = hash && hash.length > 1 ? doc.querySelector(hash) : null;
      if (target) target.scrollIntoView();
      else doc.defaultView?.scrollTo(0, 0);
    };

    if (!isGenerating) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      apply(true);
      return;
    }

    // Re-parsing the document on every token would cost hundreds of passes per
    // run for no visible gain — ~10fps already reads as live typing.
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => { timerRef.current = null; apply(false); }, 100);
  }, [ready, page?.html, page?.path, css, js, icon, isGenerating, editMode, wireframe]);

  // ── Visual editing ────────────────────────────────────────────────────────
  // Selection, typing and the outline all live inside the frame's document.
  // Nothing here calls the model or the API; the only output is the selected
  // element handed up to the toolbar, and a "something changed" ping.
  //
  // The callbacks are read through refs, so the effect below depends on the
  // frame and the mode alone. The workspace passes onEditDirty as a fresh
  // arrow on every render, and with it in the list every render tore edit
  // mode down and set it up again; the teardown clears the selection. So the
  // first change of a session (its re-render) wiped the outline while the bar
  // went on editing that element, and a heading being typed into stopped
  // taking keys.
  const onSelectRef = useRef(onSelect);
  const onEditDirtyRef = useRef(onEditDirty);
  useEffect(() => { onSelectRef.current = onSelect; onEditDirtyRef.current = onEditDirty; });

  useEffect(() => {
    if (!ready) return;
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;

    if (!editMode) { removeEditorStyle(doc); return; }

    injectEditorStyle(doc);

    const onPick = (e) => {
      // Capture phase, before the site's own handlers: a generated nav toggle
      // or carousel button would otherwise fire while the user is trying to
      // select it.
      e.preventDefault();
      e.stopPropagation();
      // The element clicked, or the picture, video or player under it when
      // the click landed on an overlay: see pickAt.
      const el = pickAt(doc, e.target, e.clientX, e.clientY);
      onSelectRef.current?.(select(doc, el));
    };

    // Typing inside a contenteditable selection is the edit; nothing to apply,
    // we just need the caller to know the document has diverged from the source.
    const onInput = () => onEditDirtyRef.current?.();

    const onKey = (e) => {
      if (e.key === 'Escape') { clearSelection(doc); onSelectRef.current?.(null); }
    };

    doc.addEventListener('click', onPick, true);
    doc.addEventListener('input', onInput, true);
    doc.addEventListener('keydown', onKey, true);
    return () => {
      doc.removeEventListener('click', onPick, true);
      doc.removeEventListener('input', onInput, true);
      doc.removeEventListener('keydown', onKey, true);
      removeEditorStyle(doc);
    };
  }, [ready, editMode]);

  // ── Keep navigation inside the frame ──────────────────────────────────────
  useEffect(() => {
    if (!ready || editMode) return;
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;

    const onClick = (e) => {
      const anchor = e.target?.closest?.('a[href]');
      if (!anchor) return;

      const decision = resolveLink(anchor.getAttribute('href'), pageList, current);

      switch (decision.kind) {
        case 'hash': {
          e.preventDefault();
          const el = decision.hash.length > 1 ? doc.querySelector(decision.hash) : null;
          if (el) el.scrollIntoView({ behavior: 'smooth' });
          else doc.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        case 'page':
          e.preventDefault();
          navigate(decision.path, decision.hash);
          return;
        case 'external':
          // The frame is sandboxed without allow-top-navigation, so following
          // this in place would silently do nothing. Open it properly instead.
          e.preventDefault();
          window.open(decision.href, '_blank', 'noopener,noreferrer');
          return;
        case 'missing':
          // A link the model wrote to a page it never generated. Following it
          // would leave the preview and land on BuildersLab's own 404.
          e.preventDefault();
          onBlockedLink?.(decision.href);
          return;
        default:
          e.preventDefault();
      }
    };

    // Generated forms should behave like the demo they are: a submit that is
    // not handled would navigate the frame away exactly like a bad link.
    const onSubmit = (e) => { if (!e.defaultPrevented) e.preventDefault(); };

    doc.addEventListener('click', onClick, true);
    doc.addEventListener('submit', onSubmit);
    return () => {
      doc.removeEventListener('click', onClick, true);
      doc.removeEventListener('submit', onSubmit);
    };
  }, [ready, editMode, pageList, current, navigate, onBlockedLink]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={PREVIEW_SHELL}
      onLoad={() => setReady(true)}
      className={className}
      style={interactive ? undefined : { pointerEvents: 'none' }}
      sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
    />
  );
});

export default LivePreviewFrame;
